/**
 * @keystone/web entry. +field (docs/design/zones.md): the game opens into character creation (no
 * character yet) or straight into the character's map (world/worldApp.ts: town, fields, the
 * levelled starter dungeon). Town NPCs open the KMMO meta screens as overlays; the Dungeon Board
 * starts an M+ key (session → Run Rewards → back to the world). The profile lives in localStorage
 * (meta/profileStore.ts) or on the server.
 * URL params: ?slot=-1|0..3&seed=&level=&dungeon=&preset=&affix= start an M+ key at once (slot -1
 * spectates); ?renderer=toon|placeholder picks the renderer; ?map=<id>&lvl=<n>&class=<id> boots a
 * throwaway offline character onto that map (screenshots; never saved).
 */
import './debug/capture'; // +capture: ?capture=1 virtual clock + trailer camera (first, before anything schedules frames)
import './hud/hud.css';
import './meta/ui/ui.css';
import './meta/kmmo/kmmo.css';
import type { BotSkillPreset, ContentRegistry } from '@keystone/sim';
import { DUNGEON, buildRegistry } from '@keystone/content';
import { type Session, type SessionEnd, startSession } from './game/session';
import { showLoading } from './hud/loading';
import { BOT_PRESET_NAMES, type StartConfig, showEndScreen } from './hud/screens';
import type { MetaContext, StartRunRequest } from './meta/context';
import { HubPrefsStore } from './meta/hubPrefs';
import { ProfileStore } from './meta/profileStore';
import { ApiClient } from './meta/api';
import { CONNECT_TIMEOUT_MS, type RunTicket, connectWithin, finishOnlineRun, startOnlineRun, withProfile } from './meta/online';
import { buildPartyRegistry, companionPersonas } from '@keystone/progression';
import { PartyClient } from './net/partyClient';
import { netSession } from './net/netSession';
import { type NetEnd, type NetRunStart, NET_BUILD, newPartyCode, parseInvite } from './net/protocol';
import { openPartyPanel } from './meta/screens/party';
import { createRouter, domScreenHost, ROUTES } from './meta/router';
import { WorldApp } from './world/worldApp'; // +field
import { bootRoute, parseDebugBoot, type DebugBoot } from './world/bootRoute'; // +field
import { MAP } from '@keystone/content';
import { PROFILE_CLASSES, addXp, createCharacter as createCharacterOp, fieldMove as fieldMoveOp, newProfile, xpToNext as xpToNextSafe } from '@keystone/progression';
import { type AutoClaims, type RunSetup, autoClaimAfterRun, clampKey, finishRun, prepareRun, runSeed } from './meta/run';
import { registerMetaScreens } from './meta/screens/index';
// +tutorial: guided first run + run coaching tips (apps/web/src/tutorial, docs/design/tutorial.md).
import { createTutorialApp } from './tutorial/app';
import { coachHooks } from './tutorial/coach';
import { inRunStart } from './meta/inrunTally';
import { heroFor } from './meta/heroes';
import type { RewardParams } from './meta/screens/reward';
import { releaseHeroStageGL } from './meta/kmmo/heroStage';
import { resolveUiScale, uiScaleSettings } from './hud/uiScale';
import { localDateString } from './meta/forge/localDate';
import { hideHoverCard, kmToast } from './meta/kmmo/components';
import { mountSettingsPanel, toggleSettingsPanel } from './hud/settingsPanel';
import { createPlaceholderRenderer } from './render/placeholder/index';
import { type GraphicsSettings, type RendererKind, loadSettings, rendererFromParam, saveSettings } from './render/settings';
import { loadToonAssets } from './render/toon/assets';
import { createToonRenderer } from './render/toon/index';
import type { Renderer } from './render/types';

const canvasHost = document.getElementById('app') as HTMLElement;
const hudHost = document.getElementById('hud') as HTMLElement;

function fatal(msg: string, err: unknown): void {
  console.error(msg, err);
  hudHost.innerHTML = '';
  const box = document.createElement('div');
  box.className = 'screen';
  box.innerHTML = `<div class="screen-box"><h2>${msg}</h2><pre></pre></div>`;
  box.querySelector('pre')!.textContent = String(err instanceof Error ? (err.stack ?? err.message) : err);
  hudHost.appendChild(box);
}

let content: ContentRegistry;
try {
  content = buildRegistry();
} catch (err) {
  fatal('Content failed to load', err);
  throw err;
}

const params = new URLSearchParams(location.search);
const preferredDungeon = [params.get('dungeon'), DUNGEON.venomAltar, DUNGEON.trainingGrounds].find((id) => id && content.dungeon(id));
const presetParam = params.get('preset') as BotSkillPreset | null;
let settings: GraphicsSettings = loadSettings();
const rendererParam = rendererFromParam(params.get('renderer'));
if (rendererParam) settings = { ...settings, renderer: rendererParam };
const affixParam = params.get('affix');
/** A ?seed= pins every run's seed (debugging); otherwise each run derives one from the profile. */
const fixedSeed = params.has('seed') ? clampInt(params.get('seed'), -1e9, 1e9, 1) : null;

// +field: ?map=… boots a throwaway character (memory-only store, never written to the real save).
const bootEnv = { hasMap: (id: string) => !!content.dungeon(id)?.field || !!content.dungeon(id)?.mobLevel, classes: PROFILE_CLASSES, homeMap: MAP.mossvale };
const debugBoot = parseDebugBoot(params, bootEnv);
const store = debugBoot ? debugStore(debugBoot) : new ProfileStore({ onError: (err, during) => console.warn(`[web] profile ${during} failed`, err) });
// +multiplayer: the Worker API (same origin; docs/design/multiplayer.md). Local mode on the Vite
// dev server, with ?offline=1, or whenever the API is unreachable.
const api = new ApiClient();
store.onRemoteError = (msg) => kmToast(msg, 'error', 4000);
store.onOffline = () => kmToast('Lost the connection to the server: playing offline with your local profile.', 'error', 6000);
/** Server ticket of the solo key being played online (null offline). */
let ticket: RunTicket | null = null;
const prefs = new HubPrefsStore();
window.addEventListener('pagehide', () => {
  world.savePosition(); // +field
  store.flush();
});
window.addEventListener('beforeunload', () => store.flush());

if (presetParam && BOT_PRESET_NAMES.includes(presetParam)) {
  const p = store.profile;
  if (p.settings.defaultBotPreset !== presetParam) store.set({ ...p, settings: { ...p.settings, defaultBotPreset: presetParam } });
}

let config: StartConfig = {
  slot: 0,
  level: clampKey(store.profile, clampInt(params.get('level'), 2, 30, prefs.get().level)),
  preset: store.profile.settings.defaultBotPreset,
  seed: fixedSeed ?? runSeed(store.profile),
  dungeonId: preferredDungeon ?? content.bundle.dungeons[0]?.id ?? '',
  // Default 'auto' = affixes by key level (PLAN §6), same as the harness.
  affixes: affixParam === null || affixParam === 'auto' ? 'auto' : affixParam === 'none' ? [] : affixParam.split(','),
  renderer: settings.renderer,
};
let spectate = params.get('slot') === '-1';

let session: Session | null = null;
let setup: RunSetup | null = null;
let closeScreen: (() => void) | null = null;
let closeSettings: (() => void) | null = null;
/** The graphics panel opened from the lobby's ⚙ menu (its own HUD gear hidden). */
let closeMetaSettings: (() => void) | null = null;
let playSeq = 0;

// Out of a run the HUD layout is not mounted, so the meta UI keeps the UI scale variable itself
// (the KMMO kit sizes everything from it; base 1.44 at 1080p).
const applyUiScale = (): void => document.documentElement.style.setProperty('--hk-scale', String(resolveUiScale(uiScaleSettings.get(), innerHeight)));
applyUiScale();
uiScaleSettings.subscribe(applyUiScale);
window.addEventListener('resize', () => {
  if (!session) applyUiScale();
});

function openGraphics(): void {
  if (!closeMetaSettings) {
    closeMetaSettings = mountSettingsPanel(hudHost, () => settings, onSettings);
    hudHost.querySelector('.gfx-settings:last-of-type')?.classList.add('km-gfx');
  }
  toggleSettingsPanel(true);
}

const screenHost = domScreenHost(hudHost);
const metaCtx: MetaContext = { store, content, prefs, startRun, openGraphics, stageAllowed: () => settings.renderer !== 'placeholder' };
const router = createRouter(screenHost, metaCtx, ROUTES.lobby);
registerMetaScreens(router);
screenHost.bindEscape(router);

// +field: the world (town, fields, the levelled dungeon). Its sessions are `session` too.
const world = new WorldApp({
  canvasHost,
  hudHost,
  content,
  store,
  router,
  settings: () => settings,
  rendererFactory: (kind, registry) => rendererFactory(kind, registry),
  begin: () => {
    const seq = ++playSeq;
    partyRun = null;
    hideHoverCard();
    router.close();
    releaseHeroStageGL();
    closeMetaSettings?.();
    closeMetaSettings = null;
    closeScreen?.();
    closeScreen = null;
    if (session && session !== world.session) session.dispose();
    session = null;
    setup = null;
    return () => seq === playSeq;
  },
  started: (s) => {
    session = s;
    applyUiScale();
    if (!closeSettings) closeSettings = mountSettingsPanel(hudHost, () => settings, onSettings);
    if (import.meta.env.DEV || params.has('debug') || debugBoot) {
      (window as unknown as { __keystone?: unknown }).__keystone = {
        session: s,
        store,
        router,
        world,
        settings: () => settings,
        auto: (on = true) => world.debugAuto(on),
        levelUp: (n = 1) => world.debugLevelUp(n),
        giveXp: (n: number) => world.debugGiveXp(n),
        teleport: (mapId: string) => world.debugTeleport(mapId),
        bounties: () => world.debugBounties(),
      };
    }
  },
});
/** +field: leave the meta UI for the world (character creation when there is no character). */
function enterWorld(): void {
  world.enter();
}
metaCtx.enterWorld = enterWorld;

// +tutorial: the guided first run borrows the run plumbing below (renderer, session slot, menu).
const tutorial = createTutorialApp({
  canvasHost,
  hudHost,
  content,
  config: () => config,
  settings: () => settings,
  rendererFactory: (kind, registry) => rendererFactory(kind, registry),
  begin: () => {
    world.suspend(); // +field
    const seq = ++playSeq;
    hideHoverCard();
    router.close();
    releaseHeroStageGL();
    closeMetaSettings?.();
    closeMetaSettings = null;
    closeScreen?.();
    closeScreen = null;
    session?.dispose();
    session = null;
    setup = null;
    return () => seq === playSeq;
  },
  started: (s) => {
    session = s;
    if (!closeSettings) closeSettings = mountSettingsPanel(hudHost, () => settings, onSettings);
  },
  menu: () => menu(),
  mainClassId: () => store.profile.mainClassId,
  profileRuns: () => store.profile.runs.length,
  // +multiplayer: through the intent table so an online profile changes on the server too.
  setMainClass: (id) => void store.act('mainClass', { classId: id }),
});
metaCtx.openTutorial = () => tutorial.pick();

// ---------------------------------------------------------------------------
// +multiplayer: party rooms (docs/design/multiplayer.md §3). One PartyClient at a time; its
// `start` launches a networked session, its `end` carries the server's rewards for this player.
// ---------------------------------------------------------------------------
let party: PartyClient | null = null;
let partyUnsub: (() => void) | null = null;
/** The party run on screen: the session's end and the room's end may arrive in either order. */
let partyRun: { seed: number; dungeonName: string; session: SessionEnd | null; end: NetEnd | null; timer: ReturnType<typeof setTimeout> | null } | null = null;
const PARTY_KEY = 'keystone.party';
/** After the local run ends, wait this long for the room's rewards before showing a plain end screen. */
const PARTY_END_WAIT_MS = 15_000;

function rememberParty(code: string | null): void {
  try {
    if (code) sessionStorage.setItem(PARTY_KEY, code);
    else sessionStorage.removeItem(PARTY_KEY);
  } catch {
    /* a reload just won't rejoin */
  }
}

/** Room refusals that end the attempt (the panel falls back to Create / Join). */
const PARTY_REFUSED = new Set(['party_full', 'run_in_progress', 'party_not_found', 'no_profile']);

/** Connect to party `code`. `join`: it must exist (invite link, typed code); else create / rejoin. */
function bindParty(code: string, join = false): PartyClient {
  partyUnsub?.();
  party?.close();
  const p = new PartyClient(api.partySocketUrl(code, join), code);
  party = p;
  rememberParty(code);
  partyUnsub = p.subscribe((e) => {
    if (e.type === 'start') void playParty(e.run);
    else if (e.type === 'end') partyFinished(e.end);
    else if (e.type === 'abort') {
      kmToast(`The key ended early: ${e.reason}`, 'error', 5000);
      if (partyRun) menu();
    } else if (e.type === 'error' && PARTY_REFUSED.has(e.code) && !partyRun && party === p) {
      kmToast(e.msg, 'error', 6000);
      partyUnsub?.();
      partyUnsub = null;
      p.close();
      party = null;
      rememberParty(null);
    } else if (e.type === 'error' && (partyRun || e.code === 'build_mismatch')) kmToast(e.msg, 'error', 5000);
    else if (e.type === 'status' && e.status === 'closed' && party === p && !partyRun) rememberParty(null);
  });
  return p;
}

const partyHost: NonNullable<MetaContext['party']> = {
  current: () => party,
  create: () => bindParty(newPartyCode()),
  join: (code) => bindParty(code, true),
  leave: () => {
    partyUnsub?.();
    partyUnsub = null;
    party?.leave();
    party = null;
    rememberParty(null);
  },
};

// The room reads the hero from the server profile: tell it when the player switches hero.
store.subscribe((p, prev) => {
  if (party && p.mainClassId !== prev.mainClassId) setTimeout(() => party?.send({ t: 'refresh' }), 1200);
});

/** The room started a key (or re-sent it after a reconnect): a networked session. */
async function playParty(run: NetRunStart): Promise<void> {
  const p = party;
  if (!p || run.you < 0) return;
  // A reconnect re-sends `start` for the run on screen: keep the session (the snapshot after it resyncs).
  if (partyRun && partyRun.seed === run.seed && session) return;
  if (run.build !== NET_BUILD) return void kmToast('The game was updated: reload the page to play with this party.', 'error', 6000);
  world.suspend(); // +field
  const seq = ++playSeq;
  hideHoverCard();
  router.close();
  releaseHeroStageGL();
  closeMetaSettings?.();
  closeMetaSettings = null;
  closeScreen?.();
  closeScreen = null;
  session?.dispose();
  session = null;
  setup = null;
  partyRun = { seed: run.seed, dungeonName: content.dungeon(run.dungeonId)?.name ?? run.dungeonId, session: null, end: null, timer: null };
  let registry: ContentRegistry;
  try {
    registry = buildPartyRegistry(content.bundle, run.compiled);
  } catch (err) {
    fatal('Could not build the party for this key', err);
    return;
  }
  config = { ...config, slot: run.you, level: run.level, seed: run.seed, dungeonId: run.dungeonId, affixes: run.affixes, preset: run.botPreset };
  const factory = await rendererFactory(settings.renderer, registry);
  if (seq !== playSeq) return;
  try {
    const partyNet = netSession(p, run.controllers, run.you);
    session = startSession({
      canvasHost,
      hudHost,
      content: registry,
      party: run.classIds,
      partyNames: run.names,
      progress: inRunStart(store.profile, heroFor(store.profile.mainClassId).name),
      config,
      onEnd: partySessionEnded,
      createRenderer: factory,
      settings,
      looterId: run.looterId,
      net: partyNet,
    });
    if (!closeSettings) closeSettings = mountSettingsPanel(hudHost, () => settings, onSettings);
    // Read-only debug handle (dev builds, or ?debug: the two-browser party test reads positions from it).
    if (import.meta.env.DEV || params.has('debug')) (window as unknown as { __keystone?: unknown }).__keystone = { session, settings: () => settings, store, router, party: p, net: partyNet };
  } catch (err) {
    fatal('Could not start the party run', err);
  }
}

function partySessionEnded(data: SessionEnd): void {
  closeSettings?.();
  closeSettings = null;
  const r = partyRun;
  if (!r) return;
  r.session = data;
  r.timer = setTimeout(() => showPartyReward(true), PARTY_END_WAIT_MS);
  showPartyReward(false);
}

function partyFinished(end: NetEnd): void {
  if (end.profile) store.acceptServer(end.profile, end.version);
  const r = partyRun;
  if (!r) return;
  r.end = end;
  showPartyReward(false);
}

/** Run Rewards once both the local session and the room are done (or a plain end screen on timeout). */
function showPartyReward(timedOut: boolean): void {
  const r = partyRun;
  if (!r || !r.session || (!r.end && !timedOut)) return;
  if (r.timer !== null) clearTimeout(r.timer);
  partyRun = null;
  const data = r.session;
  const again = (): void => menu();
  const summary = r.end?.summary ? withProfile(r.end.summary as RewardParams['summary'], store) : null;
  if (!r.end || !summary) {
    kmToast(r.end?.error ?? 'The party server did not send your rewards.', 'error', 6000);
    closeScreen = showEndScreen(hudHost, data, { again, menu: () => menu() }, { menuLabel: 'Return to the lobby' });
    return;
  }
  const claims = r.end.claims ? withProfile(r.end.claims as AutoClaims, store) : null;
  if (claims?.vault) prefs.set({ lastVault: claims.vault.kind });
  const reward: RewardParams = { summary, end: { result: r.end.result, dungeonName: r.dungeonName, rows: data.rows, scoreboard: data.scoreboard }, claims, again };
  playSeq++;
  session?.dispose();
  session = null;
  applyUiScale();
  router.reset(ROUTES.reward, reward as unknown as Record<string, unknown>);
}

/** Renderer factory for a kind; the toon renderer first loads its models (once, with a loading screen). */
// Toon assets load once (module-level promise). The lobby's hero stage starts the load at boot,
// so a run's loading screen follows that shared progress instead of starting its own.
let assetProgress: [number, number, string] | null = null;
const assetListeners = new Set<(done: number, total: number, label: string) => void>();
function loadAssetsShared(): ReturnType<typeof loadToonAssets> {
  return loadToonAssets((done, total, label) => {
    assetProgress = [done, total, label];
    for (const l of assetListeners) l(done, total, label);
  });
}

async function rendererFactory(kind: RendererKind, registry: ContentRegistry): Promise<(host: HTMLElement) => Renderer> {
  if (kind === 'placeholder') return createPlaceholderRenderer;
  const loading = showLoading(hudHost);
  const onProgress = (done: number, total: number, label: string): void => loading.set(done, total, label);
  if (assetProgress) onProgress(...assetProgress);
  assetListeners.add(onProgress);
  try {
    const assets = await loadAssetsShared();
    if (assets.failed.size) console.warn(`[web] ${assets.failed.size} asset(s) failed to load; those use fallbacks:`, [...assets.failed]);
    return (host) => createToonRenderer(host, { assets, content: registry, settings });
  } catch (err) {
    console.error('[web] toon assets failed to load; using the placeholder renderer', err);
    return createPlaceholderRenderer;
  } finally {
    assetListeners.delete(onProgress);
    loading.close();
  }
}

function onSettings(next: GraphicsSettings): void {
  const prevKind = settings.renderer;
  settings = next;
  config = { ...config, renderer: next.renderer };
  saveSettings(next);
  session?.applySettings(next);
  if (next.renderer !== prevKind && session && setup) {
    const s = session;
    void rendererFactory(next.renderer, setup.registry).then((f) => {
      if (session === s) s.setRenderer(f);
    });
  }
}

function clampInt(v: string | null, lo: number, hi: number, dflt: number): number {
  const n = v === null ? NaN : Math.round(Number(v));
  return Number.isFinite(n) ? Math.min(hi, Math.max(lo, n)) : dflt;
}

/** Back to the hub (disposes any run and end screen). */
function menu(): void {
  partyRun = null; // +multiplayer
  playSeq++;
  session?.dispose();
  session = null;
  closeSettings?.();
  closeSettings = null;
  closeScreen?.();
  closeScreen = null;
  applyUiScale();
  router.reset(ROUTES.lobby);
}

/** Hub "Start key". */
function startRun(req: StartRunRequest): void {
  spectate = req.spectate;
  config = {
    ...config,
    level: clampKey(store.profile, req.level),
    preset: req.preset,
    dungeonId: req.dungeonId || config.dungeonId,
    affixes: req.affixes,
    seed: fixedSeed ?? runSeed(store.profile),
    renderer: settings.renderer,
  };
  void playKey();
}

/** +multiplayer: online, a server ticket (seed, clamped level) comes first; offline, play at once. */
async function playKey(): Promise<void> {
  ticket = null;
  if (store.online) {
    ticket = await startOnlineRun(api, { dungeonId: config.dungeonId, level: config.level, spectate });
    if (ticket) config = { ...config, level: ticket.level, seed: fixedSeed ?? ticket.seed };
    else kmToast('The server did not answer: this key will not be saved to your online profile.', 'error', 5000);
  }
  await play();
}

/** +multiplayer: the server pays an online solo key (handlers.runFinish), then Run Rewards. */
async function endOnline(data: SessionEnd, run: RunSetup, again: () => void): Promise<void> {
  const seq = playSeq;
  const t = ticket;
  ticket = null;
  const done = t ? await finishOnlineRun(api, store, t, { result: data.result, party: run.party.map((m) => ({ classId: m.classId, controller: m.controller })), botPreset: config.preset }) : null;
  if (seq !== playSeq) return;
  if (!done) {
    kmToast('This run could not be saved to your online profile.', 'error', 6000);
    closeScreen = showEndScreen(hudHost, data, { again, menu: () => menu() }, { menuLabel: 'Return to the lobby' });
    return;
  }
  if (done.claims?.vault) prefs.set({ lastVault: done.claims.vault.kind });
  const reward: RewardParams = { summary: done.summary, end: { result: done.result, dungeonName: data.dungeonName, rows: data.rows, scoreboard: data.scoreboard }, claims: done.claims, again };
  playSeq++;
  session?.dispose();
  session = null;
  applyUiScale();
  router.reset(ROUTES.reward, reward as unknown as Record<string, unknown>);
}

async function play(): Promise<void> {
  world.suspend(); // +field: save where the hero stood; the key replaces the world session
  const seq = ++playSeq;
  hideHoverCard();
  router.close();
  // The meta UI's hero stage gives the GPU (and the borrowed VRMs) back before the run renders.
  releaseHeroStageGL();
  closeMetaSettings?.();
  closeMetaSettings = null;
  closeScreen?.();
  closeScreen = null;
  session?.dispose();
  session = null;
  store.flush();
  if (config.renderer && config.renderer !== settings.renderer) {
    settings = { ...settings, renderer: config.renderer };
    saveSettings(settings);
  }
  const run = prepareRun(store.profile, content, { spectate });
  if (run.compileError) {
    console.error('[web] could not compile the party from the profile; playing with base classes', run.compileError);
    kmToast('Could not apply your gear and talents to this run (see console).', 'error', 5000);
  }
  config = { ...config, slot: run.slot };
  const factory = await rendererFactory(settings.renderer, run.registry);
  if (seq !== playSeq) return;
  try {
    setup = run;
    session = startSession({ canvasHost, hudHost, content: run.registry, party: run.classIds, partyNames: run.names, progress: inRunStart(store.profile, heroFor(store.profile.mainClassId).name), config, onEnd: end, createRenderer: factory, settings, looterId: run.looterId, hooks: coachHooks(), personas: companionPersonas(store.profile, spectate ? '' : store.profile.mainClassId, run.classIds, config.seed) }); // +world-resources, +tutorial (coach), +idle: guild members you picked, else Group Finder strangers from the run seed (the run record names the same ones)
    if (!closeSettings) closeSettings = mountSettingsPanel(hudHost, () => settings, onSettings);
    // Debug handle (dev builds, or ?debug): __keystone.finish() ends the current run as a timed clear
    // (online the server still clamps the result: see handlers.clampedResult).
    if (import.meta.env.DEV || params.has('debug')) {
      const s = session;
      (window as unknown as { __keystone?: unknown }).__keystone = { session, settings: () => settings, store, router, setup, finish: (timed = true) => s.debugFinish({ timed }) };
    }
  } catch (err) {
    fatal('Could not start the run', err);
  }
}

function end(data: SessionEnd): void {
  closeSettings?.();
  closeSettings = null;
  const run = setup;
  const again = (): void => {
    config = { ...config, level: clampKey(store.profile, config.level), seed: fixedSeed ?? runSeed(store.profile) };
    void playKey();
  };
  if (run && store.online) return void endOnline(data, run, again);
  let reward: RewardParams | null = null;
  if (run) {
    try {
      const localDate = localDateString();
      const summary = finishRun(store.profile, {
        result: data.result,
        party: run.party.map((m) => ({ classId: m.classId, controller: m.controller })),
        botPreset: config.preset,
        seed: config.seed,
        kills: data.kills ?? undefined,
        // Daily Offering / Weekly Ritual: progression never reads a clock, the web passes the local date.
        localDate,
      });
      // The simplified UI claims a waiting vault and a ready Weekly Ritual for the player.
      let claims: AutoClaims | null = null;
      try {
        claims = autoClaimAfterRun(summary.profile, localDate);
        if (claims.vault) prefs.set({ lastVault: claims.vault.kind });
      } catch (err) {
        console.warn('[web] auto-claim after the run failed', err);
      }
      store.set(claims?.profile ?? summary.profile, { immediate: true });
      reward = { summary, end: { result: data.result, dungeonName: data.dungeonName, rows: data.rows, scoreboard: data.scoreboard }, claims, again };
    } catch (err) {
      console.error('[web] could not apply the run to the profile', err);
      kmToast('This run could not be saved to your profile (see console).', 'error', 6000);
    }
  }
  if (reward) {
    // The Run Rewards screen replaces the run: free the run's renderer (and its VRMs) for the stage.
    playSeq++;
    session?.dispose();
    session = null;
    applyUiScale();
    router.reset(ROUTES.reward, reward as unknown as Record<string, unknown>);
    return;
  }
  // Fallback (the profile could not be updated): the plain end screen over the run.
  closeScreen = showEndScreen(hudHost, data, { again, menu: () => menu() }, { menuLabel: 'Return to the lobby' });
}

// Warm the toon assets in the background so the lobby hero appears soon and the first run starts fast.
if (settings.renderer !== 'placeholder') void loadAssetsShared().catch(() => undefined);

// +multiplayer: wait (briefly) for the server profile; the game starts locally if it can't be had.
void connectWithin(CONNECT_TIMEOUT_MS, store, api, { offline: params.get('offline') === '1' }).then((mode) => {
  console.info(`[web] profile mode: ${mode}`);
  // +multiplayer: an invite link (/p/CODE or ?party=CODE) joins that party straight away.
  const invite = parseInvite(location.href);
  if (invite) {
    // Clean the address bar so a refresh doesn't rejoin a stale party (sessionStorage covers reloads).
    const q = new URLSearchParams(location.search);
    q.delete('party');
    history.replaceState(null, '', `/${q.toString() ? `?${q}` : ''}${location.hash}`);
  }
  if (mode === 'online') {
    metaCtx.party = partyHost; // +multiplayer
    let code: string | null = null;
    try {
      code = sessionStorage.getItem(PARTY_KEY);
    } catch {
      code = null;
    }
    if (invite) bindParty(invite, invite !== code);
    else if (code) bindParty(code); // a reload rejoins (mid-run: the room re-sends start + a snapshot)
  } else if (invite) kmToast('Party invites need the online server; playing offline.', 'error', 6000);
  if (params.has('slot') && !invite) void playKey();
  else {
    // +field: character creation, or straight into the character's map (no tutorial offer).
    const route = bootRoute(store.profile, params, bootEnv);
    if (route.kind === 'create') router.reset(ROUTES.create, { preselect: route.preselect });
    else enterWorld();
    if (invite && party) openPartyPanel(metaCtx);
  }
});

/** +field: a memory-only profile with a character of `b.classId` at level `b.level` on `b.mapId`. */
function debugStore(b: DebugBoot): ProfileStore {
  const s = new ProfileStore({ storage: null, defaultMainClass: b.classId });
  let p = newProfile(b.classId, 12345);
  const made = createCharacterOp(p, b.classId, b.name);
  if (made.ok) p = made.profile;
  const ch = p.characters[b.classId];
  if (ch && b.level > 1) {
    p = structuredClone(p);
    const c2 = p.characters[b.classId]!;
    let need = 0;
    for (let l = 1; l < b.level; l++) need += xpToNextSafe(l);
    addXp(c2, need);
  }
  const moved = fieldMoveOp(p, b.mapId, null, 0);
  if (moved.ok) p = moved.profile;
  s.set(p, { immediate: true });
  return s;
}
